import { forwardRef, PropsWithChildren, useContext } from 'react'

import { css } from '@emotion/react'
import { Global } from '@emotion/react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'
import FocusLock from 'react-focus-lock'

import { breakpoints, spacing } from '@cash-design-system/react'

import { ModalActionContext } from './ModalActionContext'
import { ModalActionProvider } from './ModalActionProvider'
import { ModalBackAction, ModalCloseAction } from './ModalActions'
import { ModalContextProvider } from './ModalContext'
import { ModalPaneHeader } from './ModalPaneHeader'

const ModalPaneScrollable = styled.div`
  overflow-y: auto;
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  position: relative;
  height: calc(100% - 5.5rem);
`
const ModalPaneContent = styled.div`
  padding: ${spacing.defaultContent} ${spacing.l};
  display: flex;
  justify-content: center;

  ${breakpoints.tablet} {
    padding: ${spacing.defaultContent} 0;
  }
`

const FixedPosition = styled.div`
  z-index: 99999;
  position: fixed;
  display: flex;
  flex-direction: column;
  inset: 0 0 0 0;
`

const BodyOverFlowHidden = css`
  body {
    overflow: hidden;
  }
`

const ModalPaneActions = ({ onBack, onClose }: { onBack?: () => void; onClose?: () => void }) => {
  const { actions: actionOverride } = useContext(ModalActionContext)

  const defaultActions = (
    <ModalPaneHeader justifyContent={onBack ? 'start' : 'end'}>
      {onBack ? <ModalBackAction onBack={onBack} /> : <ModalCloseAction onClose={onClose} />}
    </ModalPaneHeader>
  )
  const actions = actionOverride !== undefined ? actionOverride : defaultActions
  return actions
}

/**
 * @deprecated Use ArcadeModalPane or use ArcadeModal
 */
export const ModalPane = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{ portalRoot?: string; dataTestId?: string; onBack?: () => void; onClose?: () => void }>
>(({ portalRoot = 'modal-root', dataTestId = 'modal-pane', children, onBack, onClose }, ref) => {
  return createPortal(
    <>
      <Global styles={BodyOverFlowHidden} />
      {/* 
          FocusLock was added for backwards compatibility with the regular modal 
          please remove once full screen modals are fully launched
          */}
      <FocusLock autoFocus={false}>
        <FixedPosition role="dialog" aria-modal="true" data-testid={dataTestId} ref={ref}>
          <ModalContextProvider>
            <ModalActionProvider>
              <ModalPaneActions onBack={onBack} onClose={onClose} />
              <ModalPaneScrollable>
                <ModalPaneContent>{children}</ModalPaneContent>
              </ModalPaneScrollable>
            </ModalActionProvider>
          </ModalContextProvider>
        </FixedPosition>
      </FocusLock>
    </>,
    document.getElementById(portalRoot) as HTMLElement
  )
})

ModalPane.displayName = 'ModalPane'
